import { useEffect, useState } from 'react'

/** App-splash minimum display time so the animated loading icon is actually seen. */
const MIN_SPLASH_MS = 1000
// Module-load time ≈ app start (this module is imported by the guard chain).
const APP_START = performance.now()

/**
 * True once the app has been up for MIN_SPLASH_MS. Used by the LAST guard in
 * the splash chain (TeamGuard) to hold the loading screen long enough for the
 * animation to register — apply it in one place only, or holds stack.
 * Measured from app start, so slow real loads add zero extra delay.
 */
/**
 * Extends a query's isLoading so an in-content LoadingPanel stays visible for
 * MIN_SPLASH_MS — long enough for the animated icon to register. Holds ONLY
 * when the page mounted while actually loading; cache hits render immediately
 * with no artificial delay.
 */
export function useMinimumLoading(isLoading: boolean): boolean {
  const [mountedLoadingAt] = useState(() => (isLoading ? performance.now() : null))
  const [holdDone, setHoldDone] = useState(mountedLoadingAt === null)
  useEffect(() => {
    if (holdDone || mountedLoadingAt === null) {
      return undefined
    }
    const timer = setTimeout(() => {
      setHoldDone(true)
    }, MIN_SPLASH_MS - (performance.now() - mountedLoadingAt))
    return () => {
      clearTimeout(timer)
    }
  }, [holdDone, mountedLoadingAt])
  return isLoading || !holdDone
}

export function useMinimumSplash(): boolean {
  const [done, setDone] = useState(() => performance.now() - APP_START >= MIN_SPLASH_MS)
  useEffect(() => {
    if (done) {
      return undefined
    }
    const timer = setTimeout(() => {
      setDone(true)
    }, MIN_SPLASH_MS - (performance.now() - APP_START))
    return () => {
      clearTimeout(timer)
    }
  }, [done])
  return done
}
